import { ExternalLink } from 'lucide-react';

import { useSyntaxStyle } from '../hooks/useSyntaxStyle';
import { buildGitHubUrl, extractGitHubMeta, formatSourceLabel } from '../lib/github-url';
import { SyntaxHighlighter } from '../lib/syntax';
import { CopyButton } from './CopyButton';
import { StreamingText } from './StreamingText';

interface CodeBlockProps {
  content: string;
  metadata?: unknown;
  /** When set, the code region scrolls within this CSS height while the header (language + copy) stays pinned. */
  maxHeight?: string;
}

function getLanguage(metadata: unknown): string | null {
  if (metadata && typeof metadata === 'object' && 'language' in metadata && typeof metadata.language === 'string') {
    return (metadata as { language: string }).language;
  }
  return null;
}

export function CodeBlock({ content, metadata, maxHeight }: CodeBlockProps): React.ReactElement {
  const language = getLanguage(metadata);
  const github = extractGitHubMeta(metadata);
  const syntaxStyle = useSyntaxStyle();
  // Opt-in: cap the code region's height and scroll it, leaving the header (language + copy) pinned above.
  const scrollStyle: React.CSSProperties | undefined = maxHeight ? { maxHeight, overflow: 'auto' } : undefined;

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-grey-25">
      {github && (
        <a
          href={buildGitHubUrl(github)}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 border-b border-border px-3 py-1.5 font-mono text-[0.6875rem] text-muted-foreground transition-colors hover:bg-card hover:text-foreground"
        >
          <span className="truncate">{formatSourceLabel(github)}</span>
          <ExternalLink size={11} className="shrink-0" />
        </a>
      )}
      <div className="flex items-center justify-between border-b border-border px-3 py-1.5">
        {language ? (
          <span className="text-[0.625rem] font-medium tracking-wider text-muted-foreground uppercase">{language}</span>
        ) : (
          <span />
        )}
        <CopyButton text={content} variant="tertiary" />
      </div>
      {language === 'markdown' ? (
        <div className="p-3 text-sm" style={scrollStyle}>
          <StreamingText content={content} />
        </div>
      ) : language ? (
        <SyntaxHighlighter
          language={language}
          style={syntaxStyle}
          customStyle={{
            margin: 0,
            padding: '0.75rem',
            fontSize: '0.8125rem',
            ...scrollStyle,
          }}
        >
          {content}
        </SyntaxHighlighter>
      ) : (
        <pre className="overflow-x-auto p-3" style={scrollStyle}>
          <code className="text-[0.8125rem] text-foreground">{content}</code>
        </pre>
      )}
    </div>
  );
}
